import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { memo, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { pick, pipe, equals, pluck } from "ramda";
import { Div, TertiarySmall } from "@base-ui";
import GraphFocusLabels from "@dataVisualizations/graphFocusLabels/GraphFocusLabels";
import OrganizationsList from "@dataVisualizations/organizationslist";
import { Chart, OrganizationsHolder, GraphContainer } from "./barchart.styles";
import BarChartGraph from "./BarchartGraph";
import { getChildOrganizations } from "@webapi/api";
import { useMeasurementsWithMetadata } from "@webapi/hooks/useMeasurementsWithMetadata";
import { useAuthContext } from "@authentication/context";
import { extractValue } from "utils/measurement";
import { sortOrganizationIdsByPeriodValue } from "utils/sort";
import { addVolatileUnits, copyUnitsIntoVolatileList, selectUnitList, getVolatileId, removeUnitsFromSelectedList, useUnitListsContext } from "@unitLists";
import { usePrevious } from "hooks";
import { measurementsParams, adjustQueryToMeasurementsWithMetadata } from "utils/measurementsWithMetadata";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default /*#__PURE__*/memo(function Barchart(_ref) {
  var _adjustedQuery$sort;
  var query = _ref.query,
    setQuery = _ref.setQuery,
    measure = _ref.measure,
    lookupOrganization = _ref.lookupOrganization,
    disableGraphInteractions = _ref.disableGraphInteractions,
    disableUnitsInteraction = _ref.disableUnitsInteraction,
    renderLatestExisting = _ref.renderLatestExisting;
  var _useAuthContext = useAuthContext(),
    getUser = _useAuthContext.getUser;
  var authTicket = getUser().ticket;
  var _useMeasurementsWithM = useMeasurementsWithMetadata(measurementsParams(query)),
    measurementsWithMetadata = _useMeasurementsWithM.measurementsWithMetadata,
    loading = _useMeasurementsWithM.loading;
  var measureMeasurementsWithMetadata = useMemo(function () {
    return measurementsWithMetadata.get(measure.measureId) || [];
  }, [measure.measureId, measurementsWithMetadata]);
  var adjustedQuery = useMemo(function () {
    return renderLatestExisting ? adjustQueryToMeasurementsWithMetadata(query, measureMeasurementsWithMetadata) : query;
  }, [renderLatestExisting, query, measureMeasurementsWithMetadata]);
  var _useUnitListsContext = useUnitListsContext(),
    setUnitLists = _useUnitListsContext.setUnitLists;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    focusId = _useState2[0],
    setFocusId = _useState2[1];
  var prevFocusId = usePrevious(focusId);
  var containerRef = useRef();
  var drilldown = useCallback(function (measureId, organizationId) {
    return getChildOrganizations(measureId, organizationId, authTicket).then(function (result) {
      return setUnitLists(pipe(function (_unitLists) {
        return copyUnitsIntoVolatileList(_unitLists, pluck("id", result).toObjectEntries("id"));
      }, function (_unitLists) {
        return selectUnitList(_unitLists, getVolatileId(_unitLists));
      }));
    });
  }, [setUnitLists, authTicket]);
  var translatedMeasureUnit = (measure === null || measure === void 0 ? void 0 : measure.measureUnit) === "%" ? "Procent" : measure === null || measure === void 0 ? void 0 : measure.measureUnit;
  useEffect(function () {
    setFocusId(null);
  }, [adjustedQuery]);
  return /*#__PURE__*/_jsxs(Div, {
    children: [/*#__PURE__*/_jsx(GraphFocusLabels, {
      focusId: focusId,
      setFocusId: setFocusId,
      measure: measure,
      measurementsWithMetadata: measureMeasurementsWithMetadata,
      targetMeasurements: measure === null || measure === void 0 ? void 0 : measure.targetMeasurements,
      graphSelected: adjustedQuery.graphSelected
    }), /*#__PURE__*/_jsxs(Chart, {
      className: "chart",
      children: [/*#__PURE__*/_jsx(OrganizationsHolder, {
        children: /*#__PURE__*/_jsx(OrganizationsList, {
          query: adjustedQuery,
          setQuery: setQuery,
          measure: measure,
          lookupOrganization: lookupOrganization,
          measurementsWithMetadata: measureMeasurementsWithMetadata,
          drilldown: drilldown,
          disableUnitsInteraction: disableUnitsInteraction,
          extractValueFunc: extractValue("-"),
          sortFunc: sortOrganizationIdsByPeriodValue(lookupOrganization, ((_adjustedQuery$sort = adjustedQuery.sort) === null || _adjustedQuery$sort === void 0 ? void 0 : _adjustedQuery$sort[measure.measureId]) || adjustedQuery.sort, adjustedQuery.periodType, adjustedQuery.dateTo, adjustedQuery.units),
          addOrganization: function addOrganization(id) {
            return setUnitLists(function (old) {
              return addVolatileUnits(old, [id]);
            });
          },
          removeOrganization: function removeOrganization(id) {
            return setUnitLists(function (old) {
              return removeUnitsFromSelectedList(old, [id]);
            });
          }
        })
      }), /*#__PURE__*/_jsxs(GraphContainer, {
        ref: containerRef,
        children: [/*#__PURE__*/_jsxs(TertiarySmall, {
          className: "tertiary-small",
          style: {
            margin: 0,
            textAlign: "right"
          },
          children: ["M\xE5ttenhet: ", translatedMeasureUnit]
        }), /*#__PURE__*/_jsx(BarChartGraph, {
          query: adjustedQuery,
          measure: measure,
          measurementsWithMetadata: measureMeasurementsWithMetadata,
          containerRef: containerRef,
          loading: loading,
          lookupOrganization: lookupOrganization,
          interactivity: !disableGraphInteractions,
          focusId: focusId,
          prevFocusId: prevFocusId
        })]
      })]
    })]
  });
}, function (prev, curr) {
  return equals(pick(["query", "loading", "measurementsWithMetadata", "measure"], prev), pick(["query", "loading", "measurementsWithMetadata", "measure"], curr));
});